import { TimeRangeConfig, TimeRangeKind } from '@/api/generated/model';
import { formatToLocaleDateValue } from '@/utils/date.util';
import dayjs from 'dayjs';

export function getDateTRangeFromKind(timerange: TimeRangeConfig, now: Date = new Date()) {
  if (timerange.kind === TimeRangeKind.custom) {
    return timerange;
  }

  return getDateTRangeFromKindWithoutCustom(timerange.kind, now);
}

/**
 * WARNING the custom kind should be outside of this function
 * @param kind
 */
export const getDateTRangeFromKindWithoutCustom = (
  kind: Exclude<TimeRangeKind, 'custom'>,
  now: Date = new Date(),
) => {
  const today = now.getTime();
  const startOfDay = dayjs(now).startOf('date').valueOf();

  let start = startOfDay;
  let end = today;

  // Helper functions
  const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0).getTime();
  const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
  const startOfQuarter = (date: Date) => {
    const quarter = Math.floor(date.getMonth() / 3); // 0-indexed
    return new Date(date.getFullYear(), quarter * 3, 1, 0, 0, 0, 0).getTime();
  };
  const endOfQuarter = (date: Date) => {
    const quarter = Math.floor(date.getMonth() / 3); // 0-indexed
    return new Date(date.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59, 999).getTime();
  };

  // Time range calculations
  if (kind === TimeRangeKind.this_month) {
    start = startOfMonth(now);
    end = endOfMonth(now);
  }

  if (kind === TimeRangeKind.this_quarter) {
    start = startOfQuarter(now);
    end = endOfQuarter(now);
  }

  // if (kind === TimeRangeKind.this_year) {
  //   start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
  //   end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999).getTime();
  // }

  if (kind === TimeRangeKind.yesterday) {
    start = startOfDay - 24 * 60 * 60 * 1000;
    end = startOfDay - 1;
  }

  if (kind === TimeRangeKind['7_days']) {
    start = new Date(now).setDate(now.getDate() - 7);
  }

  if (kind === TimeRangeKind['30_days']) {
    start = new Date(now).setDate(now.getDate() - 30);
  }

  if (kind === TimeRangeKind['3_months']) {
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    start = startOfMonth(threeMonthsAgo);
  }

  if (kind === TimeRangeKind['6_months']) {
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    start = startOfMonth(sixMonthsAgo);
  }

  if (kind === TimeRangeKind['12_months']) {
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    start = startOfMonth(twelveMonthsAgo);
  }

  if (kind === TimeRangeKind.last_month) {
    const lastMonth = new Date(now);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    start = startOfMonth(lastMonth);
    end = endOfMonth(lastMonth);
  }

  if (kind === TimeRangeKind.last_quarter) {
    const lastQuarter = new Date(now);
    const quarter = Math.floor(lastQuarter.getMonth() / 3) - 1;
    const year = quarter < 0 ? lastQuarter.getFullYear() - 1 : lastQuarter.getFullYear();
    const startQuarter = quarter < 0 ? 9 : quarter * 3;
    start = new Date(year, startQuarter, 1, 0, 0, 0, 0).getTime();
    end = new Date(year, startQuarter + 3, 0, 23, 59, 59, 999).getTime();
  }

  if (kind === TimeRangeKind['3_months_full']) {
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3); // Subtract 3 months instead of 2
    start = startOfMonth(threeMonthsAgo);

    const lastMonth = new Date(now);
    lastMonth.setMonth(lastMonth.getMonth() - 1); // Move end to last month
    end = endOfMonth(lastMonth);
  }

  if (kind === TimeRangeKind['6_months_full']) {
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6); // Subtract 6 months instead of 5
    start = startOfMonth(sixMonthsAgo);

    const lastMonth = new Date(now);
    lastMonth.setMonth(lastMonth.getMonth() - 1); // Move end to last month
    end = endOfMonth(lastMonth);
  }

  if (kind === TimeRangeKind['12_months_full']) {
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12); // Subtract 12 months instead of 11
    start = startOfMonth(twelveMonthsAgo);

    const lastMonth = new Date(now);
    lastMonth.setMonth(lastMonth.getMonth() - 1); // Move end to last month
    end = endOfMonth(lastMonth);
  }

  if (kind === TimeRangeKind.q1) {
    start = new Date(now.getFullYear(), 0, 1, 0, 0).getTime();
    end = new Date(now.getFullYear(), 2, 31, 23, 59).getTime();
  }

  if (kind === TimeRangeKind.q2) {
    start = new Date(now.getFullYear(), 3, 1, 0, 0).getTime();
    end = new Date(now.getFullYear(), 5, 30, 23, 59).getTime();
  }

  if (kind === TimeRangeKind.q3) {
    start = new Date(now.getFullYear(), 6, 1, 0, 0).getTime();
    end = new Date(now.getFullYear(), 8, 30, 23, 59).getTime();
  }

  if (kind === TimeRangeKind.q4) {
    start = new Date(now.getFullYear(), 9, 1, 0, 0).getTime();
    end = new Date(now.getFullYear(), 11, 31, 23, 59).getTime();
  }

  if (kind === TimeRangeKind.ytd) {
    start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
  }

  return {
    kind: kind,
    start: formatToLocaleDateValue(start),
    end: formatToLocaleDateValue(end),
  };
};
